import angular from 'angular';
import keycodes from '../../util/keycodes/keycodes';

/**
 * @ngdoc overview
 * @name  ui.shuttleList
 * @description
 *   Directives for creating lists where items can be shuttled between them or
 *   moved within a single list. An item list an be any element with children.
 *   List elements are decorated with the `shuttle-list` directive and child
 *   elements are decorated with the `shuttle-item` directive. This module is
 *   intended for use with `ng-repeat` where the `shuttle-list` is the parent
 *   element and the `shuttle-item` is the `ng-repeat` element.
 *******************************************************************************/
export default angular
  .module('csm.ui.shuttleList', [keycodes])
  .factory('ShuttleService', ServiceFactory)
  .directive('shuttleList', ListDirective)
  .directive('shuttleItem', ItemDirective)
  .directive('shuttleCmdHandler', CmdHandlerDirective)
  .run(runShuttleList).name;

ServiceFactory.$inject = [];
ListDirective.$inject = [];
ItemDirective.$inject = [];
CmdHandlerDirective.$inject = ['$parse'];
runShuttleList.$inject = ['$injector'];
CmdFactory.$inject = ['$rootScope', '$timeout', 'KEYCODES'];

/*******************************************************************************
 * runShuttleList */
/**
 * Make services global to this closure.
 *******************************************************************************/
/* @ngInject */ function runShuttleList($injector) {
  ShuttleCommand = $injector.invoke(CmdFactory);
  ShuttleService = $injector.get('ShuttleService');
}

var ShuttleCommand;
var ShuttleService;

/*----------------------------------------*/
/* Create $() if jQuery is not installed. */
/*----------------------------------------*/
if (!window.jQuery) window.$ = angular.element;

/*******************************************************************************
 * CmdFactory */
/**
 * Command object built from event received from `shuttle-list`.
 *******************************************************************************/
/* @ngInject */ function CmdFactory($rootScope, $timeout, KEYCODES) {
  function Command($event) {
    /*---------------------------------------------*/
    /* DOM event is in `originalEvent` for jQuery. */
    /* We need this until we stop using jQuery.    */
    /*---------------------------------------------*/
    this.event = $event.originalEvent || $event;
    this.currElement = $(this.event.currentTarget);
    this.handler = new Handler(this);

    /**
     * @ngdoc      property
     * @name       ui.shuttleList.type:ShuttleCommand#targetCtrl
     * @propertyOf ui.shuttleList.type:ShuttleCommand
     * @returns    {Object} Target list controller.
     */
    this.targetCtrl = ShuttleService.listController(this.currElement);
  }

  Command.prototype = {
    /**
     * @ngdoc      property
     * @name       ui.shuttleList.type:ShuttleCommand#ctrlKey
     * @propertyOf ui.shuttleList.type:ShuttleCommand
     * @returns    {Boolean} `true` if **Ctrl** key is down.
     */
    get ctrlKey() {
      return this.event.ctrlKey;
    },
    get click() {
      return this.event.type === 'click';
    },
    get dragEnd() {
      return this.event.type === 'dragend';
    },
    get dragEnter() {
      return this.event.type === 'dragenter';
    },
    get dragLeave() {
      return this.event.type === 'dragleave';
    },
    get dragOver() {
      return this.event.type === 'dragover';
    },
    get dragStart() {
      return this.event.type === 'dragstart';
    },
    get drop() {
      return this.event.type === 'drop';
    },
    get keyDown() {
      return this.event.type === 'keydown';
    },
    get left() {
      return this.keyDown && this.event.keyCode === KEYCODES.LEFT_ARROW;
    },
    get right() {
      return this.keyDown && this.event.keyCode === KEYCODES.RIGHT_ARROW;
    },
    get up() {
      return this.keyDown && this.event.keyCode === KEYCODES.UP_ARROW;
    },
    get down() {
      return this.keyDown && this.event.keyCode === KEYCODES.DOWN_ARROW;
    },
    get lateral() {
      return this.left || this.right;
    },
    get vertical() {
      return this.up || this.down;
    },

    /**
     * @ngdoc      property
     * @name       ui.shuttleList.type:ShuttleCommand#dragMode
     * @propertyOf ui.shuttleList.type:ShuttleCommand
     * @returns    {Boolean} `true` if an item is being dragged.
     */
    get dragMode() {
      return this.dragEnter || this.dragOver || this.dragLeave || this.drop;
    },

    /**
     * @ngdoc      property
     * @name       ui.shuttleList.type:ShuttleCommand#dragElement
     * @propertyOf ui.shuttleList.type:ShuttleCommand
     * @returns    {Element} Item being dragged. `null` if not `dragMode`.
     */
    get dragElement() {
      return this.dragMode ? ShuttleService.dragElement : null;
    },

    /**
     * @ngdoc      property
     * @name       ui.shuttleList.type:ShuttleCommand#dropElement
     * @propertyOf ui.shuttleList.type:ShuttleCommand
     * @returns    {Element} Element on which dragged item will be dropped. `null` if not `dragMode`.
     */
    get dropElement() {
      return this.dragMode ? this.currElement : null;
    },

    /**
     * @ngdoc      property
     * @name       ui.shuttleList.type:ShuttleCommand#dropOnList
     * @propertyOf ui.shuttleList.type:ShuttleCommand
     * @returns    {Boolean} `true` if `dropMode` and `dropElement` is `shuttle-list` element.
     */
    get dropOnList() {
      return this.dragMode && !ShuttleService.itemController(this.dropElement);
    },

    /**
     * @ngdoc      property
     * @name       ui.shuttleList.type:ShuttleCommand#sourceElem
     * @propertyOf ui.shuttleList.type:ShuttleCommand
     * @returns    {Element} Item element from source list.
     */
    get sourceElem() {
      return this.dragMode ? this.dragElement : this.currElement;
    },

    /**
     * @ngdoc      property
     * @name       ui.shuttleList.type:ShuttleCommand#sourceCtrl
     * @propertyOf ui.shuttleList.type:ShuttleCommand
     * @returns    {Object} `sourceElem` `shuttle-list` controller.
     */
    get sourceCtrl() {
      return ShuttleService.listController(this.sourceElem);
    },

    /*-------------*/
    /* Non-public. */
    /*-------------*/
    startDrag: function () {
      ShuttleService.dragElement = this.sourceElem;
    },
    stopDrag: function () {
      ShuttleService.dragElement = null;
    },
    focusItem: focusItem,
    handleAction: handleAction,
    moveItem: moveItem
  };

  function Handler(cmd) {
    this.cmd = cmd;
  }

  Handler.prototype = {
    drop: function (pos) {
      return this.cmd.handleAction({ drop: true, position: pos });
    },
    moveFocus: function () {
      return this.cmd.handleAction({
        moveFocus: true,
        up: this.cmd.up,
        down: this.cmd.down
      });
    },
    moveItem: function () {
      return this.cmd.handleAction({
        moveItem: true,
        up: this.cmd.up,
        down: this.cmd.down
      });
    },
    shuttleFocus: function (pos) {
      return this.cmd.handleAction({ shuttleFocus: true, position: pos });
    },
    shuttleItem: function () {
      return this.cmd.handleAction({ shuttleItem: true });
    },
    startDrag: function (ea) {
      return this.cmd.handleAction({ startDrag: true, effectAllowed: ea });
    },
    allowDrop: function (de, ea) {
      return this.cmd.handleAction({
        allowDrop: true,
        effectAllowed: ea,
        dropEffect: de
      });
    }
  };

  return Command;

  /*****************************************************************************
   * Command.focusItem
   *
   * Sets the tab focus to the item at `index`. `index` refers to the element in
   * the list BEFORE any changes from the the digest cycle. This method grabs the
   * element before digest and returns a function that will refocus after.
   *
   * Parameters: listCtrl - `shuttle-list` controller.
   *             index    - Index of item to focus.
   * Returns:    Function to call to set the item focus.
   *****************************************************************************/
  function focusItem(listCtrl, index) {
    if (index >= 0 && index < listCtrl.element.childElementCount) {
      if (ShuttleService.debugMode) console.log('index=' + index);

      return function () {
        /*---------------------------------------------------------------*/
        /* Re-query the element after the digest so we get the updated   */
        /* DOM from ng-repeat, not a stale pre-digest reference.         */
        /*---------------------------------------------------------------*/
        var e = $(listCtrl.element.children[index]);
        if (e[0]) e[0].focus();
        var ctrl = ShuttleService.itemController(e);
        listCtrl.tabIndex = ctrl != null ? ctrl.$index : 0;

        if (ShuttleService.debugMode) {
          console.log('tabIndex=' + listCtrl.tabIndex);
          console.log('children=' + listCtrl.element.childElementCount);
        }
      };
    } else
      return function () {
        listCtrl.tabIndex = 0;
      };
  }

  /***************************************************************************
   * Command.handleAction
   *
   * Performs the action requested by the client event handler. Any change in
   * item data or element position triggers a digest cycle.
   *
   * @param   {object}  action  Action requested.
   * @returns {Boolean} `true`.
   ***************************************************************************/
  function handleAction(action) {
    var source = this.sourceCtrl;
    var target = this.targetCtrl;
    var $index = ShuttleService.itemController(this.sourceElem)?.$index;

    if ($index === undefined || $index === null) {
      console.log('handleAction: sourceElem has no $index.');
      return false;
    }

    var apply = false;

    /*----------------------------------------------------------------*/
    /* allowDrop - Prevent the default action to establish drop zone. */
    /*----------------------------------------------------------------*/
    if (action.allowDrop && (this.dragEnter || this.dragOver)) {
      this.event.preventDefault();
      if (action.effectAllowed)
        this.event.dataTransfer.effectAllowed = action.effectAllowed;
      if (action.dropEffect)
        this.event.dataTransfer.dropEffect = action.dropEffect;
    } else if (action.drop && ShuttleService.dragElement) {
      /*--------------------------------------------------------------------------------*/
      /* drop - Move the data item from the source data to the target. No focus change. */
      /* If dropping onto an item element, insert position is before current item if    */
      /* `position` < 0, after if `position` == 0, and end of list if `position` > 0.   */
      /* If dropping onto the list element, insert position is the end of the list.     */
      /*--------------------------------------------------------------------------------*/
      var endOfList = action.position > 0 || this.dropOnList;
      var insertPos = endOfList
        ? target.data.length
        : target.findIndex(this.dropElement) + (action.position < 0 ? 0 : 1);
      apply = this.moveItem(insertPos);
    } else if (action.moveItem || action.moveFocus) {
      /*-------------------------------------------------------*/
      /* moveItem/moveFocus - Move item or focus in same list. */
      /*-------------------------------------------------------*/
      if (
        (action.up && $index > 0) ||
        (action.down && $index < source.element.childElementCount - 1)
      ) {
        /*--------------------------------------------------------*/
        /* Find index of target element to focus to or swap with. */
        /*--------------------------------------------------------*/
        var elemIndex = $index + (action.up ? -1 : 1);

        if (action.moveFocus) apply = this.focusItem(source, elemIndex);
        /*-------------------------------------------------------------------*/
        /* To move item, get the data index from the target element.         */
        /* moveItem() will move the source item to just before or just after */
        /* the target element item. Digest will reload the item elements.    */
        /*-------------------------------------------------------------------*/ else {
          /*---------------------------------------*/
          /* When moving down, set insert position */
          /* to target element item index + 1.     */
          /*---------------------------------------*/
          var pos =
            source.findIndex($(source.element.children[elemIndex])) +
            (action.down ? 1 : 0);
          apply = this.moveItem(pos, source, $index);
        }
      }
    } else if (action.shuttleFocus && target.element.childElementCount > 0) {
      /*--------------------------------------------------------------*/
      /* shuttleFocus - Move the focus to the target list. `position` */
      /* < 0 for top, > 0 for bottom, and 0 for same list position.   */
      /*--------------------------------------------------------------*/
      var newIndex =
        action.position < 0
          ? 0
          : action.position == 0
            ? $index
            : target.element.childElementCount - 1;
      apply = this.focusItem(
        target,
        Math.min(target.element.childElementCount - 1, newIndex)
      );
    } else if (action.shuttleItem)
      /*--------------------------------------------------------------*/
      /* shuttleItem - Move the data item from the source data to the */
      /* target data. Move focus in source list to next item down.    */
      /*--------------------------------------------------------------*/
      apply = this.moveItem(
        target.data.length,
        source,
        $index < source.element.childElementCount - 1
          ? $index + 1
          : source.element.childElementCount > 1
            ? $index - 1
            : -1
      );
    /*---------------------------------------*/
    /* startDrag - Save the element to drag. */
    /*---------------------------------------*/ else if (
      action.startDrag &&
      this.dragStart
    ) {
      this.startDrag();
      if (action.effectAllowed)
        this.event.dataTransfer.effectAllowed = action.effectAllowed;
    }

    /*------------------------------------------------------*/
    /* If changes were made, consume the event, trigger a   */
    /* digest cycle, and run the apply method. `apply`      */
    /* method will either set the focus to an item or noop. */
    /*------------------------------------------------------*/
    if (apply) {
      this.event.stopPropagation();

      if (this.lateral || this.vertical) this.event.preventDefault();

      /*------------------------------------------------------------*/
      /* TODO: Run a digest in each list. Done this way because     */
      /* $rootScope.digest() sometimes completes before DOM is      */
      /* updated so ListDirective listener for changes in the       */
      /* element list may not fire. This problem manifested when a  */
      /* route component (pollingAdmin.enterPoll) used this module. */
      /*------------------------------------------------------------*/
      $(this.sourceCtrl.element).scope().$digest();
      if (this.sourceCtrl !== this.targetCtrl)
        $(this.targetCtrl.element).scope().$digest();
      //      $rootScope.$digest();

      $timeout(apply, 0, ShuttleService.fullDigest);
    }

    return (this.handled = true);
  }

  /***************************************************************************
   * Command.moveItem
   *
   * Moves $item from `sourceElem` to `targetCtrl.data` and resets focus.
   *
   * @param insertPos  {integer} `targetCtrl.data` insert position.
   * @param focusList  {object}  `shuttle-list` to set focus. null=skip focus.
   * @param focusIndex {integer} Index of item element to focus in `focusList`.
   * @returns                    Function to run after digest.
   ***************************************************************************/
  function moveItem(insertPos, focusList, focusIndex) {
    /*------------------------------------------------------------*/
    /* sourceIndex = index of `sourceElem.$item` in `sourceData`. */
    /* targetIndex = `targetData` insert position.                */
    /*                                                            */
    /* If `sourceData` and `targetData` are the same list,        */
    /* `targetIndex` is compensated for change in `sourceData`.   */
    /*------------------------------------------------------------*/
    var sourceData = this.sourceCtrl.data;
    var targetData = this.targetCtrl.data;
    var sourceIndex = this.sourceCtrl.findIndex(this.sourceElem);
    var targetIndex =
      insertPos - (targetData == sourceData && sourceIndex < insertPos ? 1 : 0);

    targetData.splice(targetIndex, 0, sourceData.splice(sourceIndex, 1)[0]);

    /*------------------------------------*/
    /* Return method to run after digest. */
    /*------------------------------------*/
    return focusList ? this.focusItem(focusList, focusIndex) : angular.noop;
  }
}

/*******************************************************************************
 * CmdHandlerDirective */
/**
 * Directive that handles `ShuttleCommand` events for `shuttle-list`. The
 * expression receives `$cmd` (the `ShuttleCommand`), `$source` (the source
 * `shuttle-list` controller) and `$target` (the target `shuttle-list`
 * controller).
 *******************************************************************************/
/* @ngInject */ function CmdHandlerDirective($parse) {
  return {
    restrict: 'A',
    link: function ($scope, elem, attrs) {
      var handler = $parse(attrs.shuttleCmdHandler);

      ShuttleService.group(attrs.shuttleGroup).cmdHandler = function (
        $cmd,
        $source,
        $target
      ) {
        return handler($scope, {
          $cmd: $cmd,
          $source: $source,
          $target: $target
        });
      };
    }
  };
}

/*******************************************************************************
 * GroupEventHandlerFactory */
/**
 * Returns an event handler for a `shuttle-group` to handle `shuttle-list`
 * components. Converts the keyboard or mouse event to a command object and calls
 * `group.cmdHandler` with each `shuttle-list` as the target. Returned value is
 * true if command was handled. If `group.cmdHandler` returns `false` for each
 * list, the event is ignored.
 *
 * For keyboard and 'dragstart' events, source list contains the event target.
 * Target list is set to source list so command is offered first to the source
 * list and then to the other lists.
 *
 * In 'dragMode', source list contains the item being dragged and target list
 * contains the event target. Command is offered to target list only.
 *
 * @param  group  `GroupObject` for the `shuttle-group`.
 *******************************************************************************/
function GroupEventHandlerFactory(group) {
  return function ($event) {
    if (ShuttleService.debugMode)
      console.log(
        $event.currentTarget.nodeName +
          ':' +
          $event.target.nodeName +
          ':' +
          $($event.target).scope().$id +
          ':' +
          $event.type
      );

    if (!group.cmdHandler)
      throw new Error('shuttle-handler: Event handler method is required.');

    var cmd = new ShuttleCommand($event);

    if (
      !group.cmdHandler(cmd, cmd.sourceCtrl.element, cmd.targetCtrl.element) &&
      !cmd.dragMode
    )
      group.lists.some(function (ctrl) {
        if (!cmd.handled && cmd.sourceCtrl != (cmd.targetCtrl = ctrl))
          return group.cmdHandler(
            cmd,
            cmd.sourceCtrl.element,
            cmd.targetCtrl.element
          );
      });

    if (cmd.dragEnd) cmd.stopDrag();
  };
}

/*******************************************************************************
 * GroupObject */
/**
 * Properties: lists        - `shuttle-list` controllers in group.
 *             cmdHandler   - `shuttle-cmd-handler` method.
 * Methods:    addList      - Adds `shuttle-list` controller to `lists'.
 *             eventHandler - Handler for group events.
 *
 * Group object containing list of `shuttle-list` controllers and command
 * handler.
 *******************************************************************************/
function GroupObject() {
  this.lists = [];
  this.cmdHandler = null;
  this.eventHandler = new GroupEventHandlerFactory(this);
  this.addList = function (list) {
    this.lists.push(list);
  };
  this.removeList = function (list) {
    this.lists.removeItem(list);
  };
}

/*******************************************************************************
 * ItemDirective */
/**
 * Identifies items within the `shuttle-list`. Binds events to list group event
 * handler. Sets `listCtrl` property on controller to `shuttle-list` controller.
 *******************************************************************************/
/* @ngInject */ function ItemDirective() {
  return {
    restrict: 'A',
    require: { item: 'shuttleItem', list: '^^shuttleList' },
    controller: ItemDirectiveController,
    link: function (scope, elem, attrs, ctrls) {
      ctrls.item.listCtrl = ctrls.list;

      /*-----------------------------*/
      /* Item must be child of list. */
      /*-----------------------------*/
      if (elem.parent()[0] !== ctrls.list.element)
        throw new Error(
          'shuttle-item: "shuttle-item" must be child of "shuttle-list".'
        );

      /*---------------------------------------------------------*/
      /* Set `$item` on controller when attribute value changes. */
      /*---------------------------------------------------------*/
      scope.$watch(attrs.shuttleItem, function (value) {
        ctrls.item.$item = value;
      });

      /*--------------------------------------*/
      /* Set `tabIndex` on element to 0 for   */
      /* the tabbed item and -1 for the rest. */
      /*--------------------------------------*/
      scope.$watch(
        function () {
          return ctrls.list.tabIndex === ctrls.item.$index ? 0 : -1;
        },
        function (value) {
          elem[0].tabIndex = value;
        }
      );
    }
  };
}

/*******************************************************************************
 * ItemDirectiveController */
/**
 * `shuttle-item` controller. Binds event handlers. `drag-handle` and
 * `click-target` attributes can be used to designate child elements the user
 * must click on to affect the action. `drag-handle` for the `dragstart` event
 * and `click-target` for the click event. If none of the elements in the content
 * are not so decorated, click area for `dragstart` and `click` actions is
 * unrestricted.
 *******************************************************************************/
/* @ngInject */ function ItemDirectiveController($scope, $element) {
  var $itemCtrl = this;
  $itemCtrl.element = $element[0];
  $itemCtrl.listCtrl = null; // Set by link function.

  /*----------------------*/
  /* Bind event handlers. */
  /*----------------------*/
  var eventHandler = function ($event) {
    $itemCtrl.listCtrl.handleEvent($event);
  };

  $element.on('drop', eventHandler);
  $element.on('dragend', eventHandler);
  $element.on('dragover', eventHandler);
  $element.on('keydown', eventHandler);

  /*---------------------------------------------------------*/
  /* If there is a clickable zone element in the transcluded */
  /* content, `drag-handle` or `click-target`, ignore the    */
  /* event if the zone was not the last mousedown target.    */
  /* From: https://stackoverflow.com/a/31004218/2245849.     */
  /*---------------------------------------------------------*/
  var clickTarget = false;
  var dragHandle = false;
  var mouseTarget = false;

  this.$onInit = function () {
    clickTarget = $element[0].querySelector('[click-target]');
    dragHandle = $element[0].querySelector('[drag-handle]');
  };

  var zoneHandler = function ($event, zone, handler) {
    if (zone && !(mouseTarget instanceof Node && zone.contains(mouseTarget)))
      $event.preventDefault();
    else handler($event);
  };

  $element.on('click', function ($event) {
    zoneHandler($event, clickTarget, eventHandler);
  });
  $element.on('dragstart', function ($event) {
    zoneHandler($event, dragHandle, eventHandler);
  });
  $element.on('mousedown', function ($event) {
    mouseTarget = $event.target;
  });

  /*--------------------------------------------------------------*/
  /* 'dragleave' fires for parent items on child mouseover but    */
  /* after 'dragenter' fires on the child. We save 'event.target' */
  /* on enter and fire leave only if 'event.target' is the same.  */
  /* This maintains the integrity of the enter/leave event cycle. */
  /* From: https://stackoverflow.com/a/26459269/2245849.          */
  /*--------------------------------------------------------------*/
  var dragTarget = false;
  var targetHandler = function ($event, condition) {
    if (condition) {
      $event.stopPropagation();
      $event.preventDefault();
      eventHandler($event);
    }
  };

  $element.on('dragenter', function ($event) {
    targetHandler($event, (dragTarget = $event.target));
  });
  $element.on('dragleave', function ($event) {
    targetHandler($event, $event.target == dragTarget);
  });
}

/*******************************************************************************
 * ListDirective */
/**
 * Identifies items within the `shuttle-list`. Binds events to list group event
 * handler.
 *******************************************************************************/
/* @ngInject */ function ListDirective() {
  return {
    restrict: 'A',
    controller: ListDirectiveController,
    link: function (scope, elem, attrs, ctrl) {
      ctrl.groupName = attrs.shuttleGroup;
      ctrl.keyField = attrs.shuttleKeyField;

      ShuttleService.group(ctrl.groupName).addList(ctrl);

      /*-----------------------------------------------------------*/
      /* Set `data` property on controller when list data changes. */
      /*-----------------------------------------------------------*/
      scope.$watch(attrs.shuttleList, function (value) {
        ctrl.data = value;
      });

      /*--------------------------------------------------------*/
      /* If the item at `ctrl.tabIndex` is moved from the list, */
      /* this will normalize `tabIndex` to the list length.     */
      /*--------------------------------------------------------*/
      scope.$watch(
        function () {
          return ctrl.tabIndex < elem[0].childElementCount;
        },
        function (value) {
          ctrl.tabIndex = Math.max(
            0,
            Math.min(ctrl.tabIndex, elem[0].childElementCount - 1)
          );
        }
      );

      /*----------------------------------------------*/
      /* When the list element child elements change  */
      /* in size or sort or order, set the `$index`   */
      /* property on all the `shuttle-item` elements. */
      /*----------------------------------------------*/
      scope.$watchCollection(
        function (scope) {
          if (ShuttleService.debugMode)
            console.log('children=' + elem[0].children.length);
          return elem[0].children;
        },
        function (value) {
          for (
            var i = 0, node = value[0];
            node;
            node = node.nextElementSibling, i++
          ) {
            ShuttleService.itemController($(node)).$index = i;
            if (ShuttleService.debugMode) console.log('$index=' + i);
          }
        }
      );

      /*-----------------------------------*/
      /* Remove the managed list from the  */
      /* group when the scope is destroyed */
      /*-----------------------------------*/
      scope.$on('$destroy', function () {
        ShuttleService.group(ctrl.groupName).removeList(ctrl);
      });
    }
  };
}

/*******************************************************************************
 * ListDirectiveController */
/**
 * `shuttle-list` controller. Binds event handlers and provides `findIndex`,
 * which returns the index of an item in `data`.
 *******************************************************************************/
/* @ngInject */ function ListDirectiveController($scope, $element, $timeout) {
  var $listCtrl = this;
  this.element = $element[0];
  this.tabIndex = 0;
  this.findIndex = findIndex;

  /*-----------------------*/
  /* Set by link function. */
  /*-----------------------*/
  this.data = [];
  this.groupName = null;
  this.keyField = null;

  /*----------------------------------------------------*/
  /* Event handler passes event to group event handler. */
  /*----------------------------------------------------*/
  this.handleEvent = function ($event) {
    ShuttleService.group($listCtrl.groupName).eventHandler($event);
  };

  /*----------------------------------------------------------------------*/
  /* For drag/drop on list instead of list item, ignore events for all    */
  /* but the list and pass the list element to the handler as the target. */
  /*----------------------------------------------------------------------*/
  var eventHandler = function ($event) {
    if ($event.currentTarget == $event.target)
      $listCtrl.handleEvent($event, $listCtrl.element);
  };

  $element.on('drop', eventHandler);
  $element.on('dragover', eventHandler);
  $element.on('dragenter', eventHandler);
  $element.on('dragleave', eventHandler);

  /*****************************************************************************
   * findIndex
   *
   * Retreves `$item` from `$element`, searches `$listCtrl.data` for `$item` and
   * returns the index. Throws an exception if not found. Used for manipulation
   * of items in `$listCtrl.data`.
   *
   * Parameters: $element - List item ngElement.
   * Returns:    Index of `$item` in `$listCtrl.data`.
   *****************************************************************************/
  function findIndex($element) {
    var $item = ShuttleService.itemController($element).$item;
    var index;

    /*-----------------------------------------------------*/
    /* Search for the item by key if there is a key field. */
    /*-----------------------------------------------------*/
    if ($listCtrl.keyField) {
      var keyValue = $item[$listCtrl.keyField];
      if (
        !$listCtrl.data.some(function (item, i) {
          return item[$listCtrl.keyField] === keyValue
            ? (index = i) || true
            : false;
        })
      )
        throw new Error(
          'findIndex: failed to find "' + $listCtrl.keyField + '" in item:',
          $item
        );
    } else if (0 > (index = $listCtrl.data.indexOf($item)))
      /*----------------------------------*/
      /* Else, search for the whole item. */
      /*----------------------------------*/
      throw new Error('findIndex: failed to find item:', $item);

    return index;
  }
}

/*******************************************************************************
 * ServiceFactory */
/**
 * Returns the shuttle service. It tracks the element being dragged and retrieves
 * or creates a group by name.
 *******************************************************************************/
/* @ngInject */ function ServiceFactory() {
  var groups = {};
  var dragElement;
  var debugMode = false;
  var fullDigest = false;

  return {
    get dragElement() {
      return dragElement;
    },
    set dragElement(e) {
      dragElement = e;
    },
    get debugMode() {
      return debugMode;
    },
    set debugMode(b) {
      debugMode = b;
    },
    get fullDigest() {
      return fullDigest;
    },
    set fullDigest(b) {
      fullDigest = b;
    },

    group: function (name) {
      var n = name || 'default';
      return groups[n] || (groups[n] = new GroupObject(this));
    },

    itemController: function (element) {
      return element.controller('shuttleItem');
    },
    listController: function (element) {
      return element.controller('shuttleList');
    }
  };
}
